/**
 * Frontend question configuration constants for the onboarding questionnaire
 *
 * This file defines all questions, answer options, and conditional logic rules
 * for the client-side risk assessment questionnaire.
 */

import { AnswerValue, QuestionKey } from '~/graphql/gen/graphql';

export { AnswerValue, QuestionKey };

/**
 * Mapping from AnswerValue enum to user-friendly display labels
 */
export const ANSWER_LABELS: Record<AnswerValue, string> = {
  [AnswerValue.Yes]: 'Yes',
  [AnswerValue.No]: 'No',
  [AnswerValue.NotSure]: 'Not Sure',
  [AnswerValue.MothersSide]: "Mother's Side",
  [AnswerValue.FathersSide]: "Father's Side",
  [AnswerValue.ImAdopted]: "I'm Adopted",
};

/**
 * Answer option sets - predefined arrays of answer options for different question types
 */
export const ANSWER_OPTION_SETS = {
  // Most questions use Yes/No/Not Sure
  YES_NO_NOT_SURE: [AnswerValue.Yes, AnswerValue.No, AnswerValue.NotSure],

  // Pregnancy and infant questions use only Yes/No
  YES_NO: [AnswerValue.Yes, AnswerValue.No],

  // Family side question uses these four options
  FAMILY_SIDE_OPTIONS: [
    AnswerValue.MothersSide,
    AnswerValue.FathersSide,
    AnswerValue.NotSure,
    AnswerValue.ImAdopted,
  ],
};

/**
 * Conditional rule defining when a question should be visible
 */
export interface ConditionalRule {
  /** The question key that this rule depends on */
  dependsOnQuestionKey: QuestionKey;
  /** The answer value that must be selected for this question to be visible */
  dependsOnAnswerValue: AnswerValue;
}

/**
 * Configuration for a single questionnaire question
 */
export interface QuestionConfig {
  /** Unique identifier for this question */
  key: QuestionKey;
  /** The question text displayed to the user */
  questionText: string;
  /** Available answer options for this question */
  answerOptions: AnswerValue[];
  /** Optional rule defining when this question is visible based on another question's answer */
  conditionalRule?: ConditionalRule;
}

/**
 * Base question configurations - all 7 questions in the initial questionnaire
 * These are ordered by their visual appearance in the questionnaire flow
 */
export const BASE_QUESTIONS: QuestionConfig[] = [
  {
    key: QuestionKey.OtcDiagnosis,
    questionText:
      'Do you know of anyone in your family who has been diagnosed with OTC deficiency?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO_NOT_SURE,
  },
  {
    key: QuestionKey.HighAmmonia,
    questionText:
      'Has anyone in your family ever had unexplained high ammonia levels or been hospitalized after illness, fasting, or a high-protein meal?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO_NOT_SURE,
  },
  {
    key: QuestionKey.UnexplainedSymptoms,
    questionText:
      'Has anyone in your family (including you) experienced unexplained fatigue, vomiting, or confusion that doctors couldn’t explain?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO_NOT_SURE,
  },
  {
    key: QuestionKey.SuddenDeath,
    questionText:
      'Has anyone in your family ever died suddenly in infancy or childhood without a clear cause?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO_NOT_SURE,
  },
  {
    key: QuestionKey.PregnancyPlanning,
    questionText:
      'Are you or is anyone in your immediate family currently pregnant or planning pregnancy within the next year?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO,
  },
  {
    key: QuestionKey.InfantBoy,
    questionText:
      'Do you currently have a newborn or infant boy(under 1 year) in your family or extended family?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO,
  },
  {
    key: QuestionKey.NewbornAmmonia,
    questionText:
      'Has any newborn in your family had high ammonia or unexplained illness after birth?',
    answerOptions: ANSWER_OPTION_SETS.YES_NO_NOT_SURE,
  },
];

/**
 * Conditional question configuration
 * This question is only shown when OTC_DIAGNOSIS is answered with YES
 */
const CONDITIONAL_QUESTION: QuestionConfig = {
  key: QuestionKey.FamilySide,
  questionText:
    'Which side of the family is that person related to you through?',
  answerOptions: ANSWER_OPTION_SETS.FAMILY_SIDE_OPTIONS,
  conditionalRule: {
    dependsOnQuestionKey: QuestionKey.OtcDiagnosis,
    dependsOnAnswerValue: AnswerValue.Yes,
  },
};

/**
 * All onboarding questions including conditional ones
 * Total: 7 base questions + 1 conditional question = 8 total questions (when conditional is visible)
 * When OTC_DIAGNOSIS is not YES, only 7 questions are shown
 */
export const ONBOARDING_QUESTIONS: QuestionConfig[] = [
  ...BASE_QUESTIONS.slice(0, 1), // OTC_DIAGNOSIS
  CONDITIONAL_QUESTION, // FAMILY_SIDE (conditional)
  ...BASE_QUESTIONS.slice(1), // All remaining base questions
];
